import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardHeader, Form } from "reactstrap";
import { Table, TableRow } from "../../../components/Table";
import { useRole } from "../../../redux/slices/authSlice";
import { gatewayPaymentStatementGet } from "../../../redux/slices/metriwsSlice";
import AddStatementModal from "../../../components/Custom/Modals/Reconciliation/AddStatementModal";
import BackIcon from "../../../components/Custom/Elements/BackIcon/BackIcon";

import {
  reconciliationMismatchesGet,
  reconciliationFilesListGet,
} from "../../../redux/slices/reconciliationSlice";
import { useForm } from "react-hook-form";
import ControlledDropDown from "../../../components/Custom/Forms/Controller/ControlledDropDown";
import ControlledInput from "../../../components/Custom/Forms/Controller/ControlledInput";
import ControlledDatePicker from "../../../components/Custom/Forms/Controller/ControlledDatePicker";
import { DROPDOWN_ALL_VALUE, RECON_LIMIT, RECON_PAGINATION_OPTIONS } from "../../../constant";
import {
  dateFormatter,
  onFixed,
  responseToaster,
  seperator,
} from "../../../helperFunctions";
import { statusDataOptions } from "../Transaction/mock";
import { getStatusStyle } from "../../../theme";
import { Box } from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import "./Reconciliation.scss";

const statusOptions = [
  { value: DROPDOWN_ALL_VALUE, label: "All" },
  ...statusDataOptions,
];

const ReconciliationFile = () => {
  const dispatch = useDispatch();
  const role = useRole("Txn Recon");

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 20,
    borderRadius: 4,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor:
        theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 4,
      backgroundColor: theme.palette.mode === "light" ? "#76bdff" : "#308fe8",
    },
  }));

  const [activeTab, setActiveTab] = useState("file"); // "mismatches" or "file"
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [filterColumns, setFilterColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddStatementOpen, setIsAddStatementOpen] = useState(false);


  const { metriwsPaymentState } = useSelector((state) => state.metriws);
  const { mismatchesData, filesData } = useSelector(
    (state) => state.reconciliation,
  );

  const [filter, setFilter] = useState({
    page_no: 1,
    limit: RECON_LIMIT,
    isFilter: false,
  });

  const mismatchesColumns = [
    {
      title: "RECON DATE",
      name: "reconciliation_date",
      Cell: (data) => {
        return (
          <div className="cell-order">
            <div className="order-singal-div">
              <p>
                <span>Recon Date : </span>
                {data?.reconciliation_date || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Created At : </span>
                {data?.created_at || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Bank ID : </span>
                {data?.bank_id || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>File ID : </span>
                {data?.file_id || ""}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "TRANSACTION DETAILS",
      name: "txn_id",
      Cell: (data) => {
        const { bgClassName } = getStatusStyle(data?.txn_status);
        return (
          <div className="cell-order">
            <div className="order-singal-div">
              <p>
                <span>Txn ID : </span>
                {data?.txn_id || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>UTR : </span>
                {data?.txn_utr || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Amount : </span>
                {data?.txn_amount ? seperator(data?.txn_amount) : ""}
              </p>
            </div>
            <div>
              <p>
                <span>Status : </span>
                <span className={`${bgClassName}`}>{data?.txn_status}</span>
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "PG DETAILS",
      name: "pg_txn_id",
      Cell: (data) => {
        const { bgClassName } = getStatusStyle(data?.pg_status);
        return (
          <div className="cell-order">
            <div className="order-singal-div">
              <p>
                <span>PG Txn ID : </span>z
                {data?.pg_txn_id || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>PG UTR : </span>
                {data?.pg_utr || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>PG Amount : </span>
                {data?.pg_amount ? seperator(data?.pg_amount) : ""}
              </p>
            </div>
            <div >
              <p>
                <span>PG Status : </span>
                <span className={`${bgClassName}`}>{data?.pg_status}</span>
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "MISMATCH REASON",
      name: "mismatch_reason",
      Cell: (data) => {
        return (
          <div className="cell-order">
            <span className="text-danger">{data?.mismatch_reason || ""}</span>
          </div>
        );
      },
    },
  ];

  const fileColumns = [
    {
      title: "FILE DETAILS",
      name: "file_name",
      Cell: (data) => {
        return (
          <div className="cell-order">
            <div className="order-singal-div">
              <p>
                <span>File Name : </span>
                {data?.file_name || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>File ID : </span>
                {data?.fileId || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Bank ID : </span>
                {data?.bank_id || ""}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Statement Date : </span>
                {data?.statement_date || "-"}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "COUNTS",
      name: "total_count",
      Cell: (data) => {
        return (
          <div className="cell-order">
            <div className="order-singal-div">
              <p>
                <span>Total Count : </span>
                {data?.total_count || 0}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Pending Count : </span>
                {data?.pending_count || 0}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Mismatch Count : </span>
                {data?.mismatch_count || 0}
              </p>
            </div>
            <div className="order-singal-div">
              <p>
                <span>Progress : </span>
                {data?.progress || 0}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "PROGRESS",
      name: "progress",
      Cell: (data) => {
        function calculateProgressPercentage(data) {
          if (data?.total_count == 0 || !data?.total_count) {
            return 0;
          }
          const completedCount = data?.total_count - data?.pending_count;
          const percentage = (completedCount / data?.total_count) * 100;
          return percentage;
        }
        const todata = calculateProgressPercentage(data);
        const progressPer = todata > 100 ? 100 : todata;
        return (
          <>
            <div className="position-relative">
              <Box sx={{ flexGrow: 1 }}>
                <BorderLinearProgress
                  variant="determinate"
                  value={progressPer}
                />
              </Box>
              <div className="progress-position text-center">
                <span>{onFixed(progressPer)}%</span>
              </div>
            </div>
          </>
        );
      },
    },
  ];

  const activeColumns =
    activeTab === "mismatches" ? mismatchesColumns : fileColumns;
  const activeData = activeTab === "mismatches" ? mismatchesData : filesData;

  const initialValues = {
    bankId: DROPDOWN_ALL_VALUE,
    date: [null, null],
    status: DROPDOWN_ALL_VALUE,
    txnId: "",
    bankRrn: "",
  };

  const { control, handleSubmit, getValues, reset } = useForm({
    defaultValues: initialValues,
  });

  const getBankOptions = () => {
    return (
      metriwsPaymentState?.data
        ?.filter?.((item) => item?.pgName !== "UPIPAY")
        ?.map?.((item) => ({
          value: item?.accountId,
          label: `${item?.pgLabel} - ${item?.pgName}`,
        })) || []
    );
  };

  const onSubmit = () => {
    const values = getValues();
    setFilter({
      ...filter,
      ...values,
      page_no: 1,
      isFilter: true,
    });
  };

  const handleRowClick = (item) => {
    if (activeTab === "file") {
      setSelectedFileId(item?.fileId);
      setActiveTab("mismatches");
      reset({
        ...initialValues,
        bankId: DROPDOWN_ALL_VALUE,
      });
      setFilter({
        page_no: 1,
        limit: RECON_LIMIT,
        isFilter: false,
      });
    }
  };

  const onGetData = async () => {
    try {
      setIsLoading(true);
      const { isFilter, page_no, limit, date, bankId, status, txnId, bankRrn } =
        filter;

      const payload = {
        page_no,
        limit,
        filter_data: {},
      };

      if (isFilter) {
        if (bankId && bankId !== DROPDOWN_ALL_VALUE && activeTab === "file") {
          payload.filter_data.bankId = bankId;
        }
        if (date?.[0]) {
          payload.filter_data.startDate = dateFormatter(date[0], "start");
        }
        if (date?.[1]) {
          payload.filter_data.endDate = dateFormatter(date[1], "end", date[0]);
        }
        if (status && status !== DROPDOWN_ALL_VALUE) {
          payload.filter_data.status = status;
        }
        if (txnId) {
          payload.filter_data.txnId = txnId;
        }
        if (bankRrn) {
          payload.filter_data.bankRrn = bankRrn;
        }
      }

      let res;
      if (activeTab === "mismatches") {
        if (selectedFileId) {
          payload.filter_data.fileId = selectedFileId;
        }
        res = await dispatch(reconciliationMismatchesGet(payload)).unwrap();
      } else {
        res = await dispatch(reconciliationFilesListGet(payload)).unwrap();
      }

      if (isFilter) {
        responseToaster(res);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(gatewayPaymentStatementGet());
  }, []);

  useEffect(() => {
    const newColumns = activeColumns?.map((item) => item?.name);
    setFilterColumns(newColumns);
  }, [activeTab]);

  useEffect(() => {
    if (role.includes("txn-recon-list")) {
      onGetData();
    }
  }, [filter, activeTab, selectedFileId]);

  if (!role.includes("txn-recon-list")) {
    return null;
  }

  return (
    <div className="support-log-main-div">
      <Card>
        <CardHeader className="d-flex justify-content-between flex-wrap align-items-center">
          <div className="d-flex align-items-center justify-content-between w-100 flex-wrap">
            <h4 className="main-title d-flex align-items-center gap">
              {activeTab === "mismatches" && selectedFileId && (
                <BackIcon
                  onClick={() => {
                    setActiveTab("file");
                    setSelectedFileId(null);
                    reset();
                    setFilter({
                      page_no: 1,
                      limit: RECON_LIMIT,
                      isFilter: false,
                    });
                  }}
                />
              )}
              <b>Txn Recon</b>
            </h4>
            <div className="d-flex button-space">
              {role.includes("add-recon-list") && activeTab === "file" && (
                <Button
                  className="filterbtn"
                  onClick={() => setIsAddStatementOpen(true)}
                >
                  Add Statement
                </Button>
              )}
            </div>
          </div>
          <Form
            className="d-flex my-xl-auto right-content align-items-end button-space flex-wrap pt-1 w-100"
            onSubmit={handleSubmit(onSubmit)}
          >
            {activeTab === "file" && (
              <ControlledDropDown
                name="bankId"
                label="Bank"
                isDefaultOption={DROPDOWN_ALL_VALUE}
                options={getBankOptions()}
                control={control}
              />
            )}
            {activeTab === "mismatches" && (
              <>
                <ControlledDatePicker
                  name="date"
                  placeholder="Select Date Range"
                  label="Date Range"
                  selectsRange
                  maxDate={new Date()}
                  control={control}
                />
                <ControlledDropDown
                  name="status"
                  label="Status"
                  isDefaultOption={false}
                  options={statusOptions}
                  control={control}
                />
                <ControlledInput
                  name="txnId"
                  placeholder="Enter Txn ID"
                  label="Txn ID"
                  control={control}
                />
                <ControlledInput
                  name="bankRrn"
                  placeholder="Enter Bank RRN"
                  label="Bank RRN"
                  control={control}
                />
              </>
            )}
            <Button type="submit" color="primary">
              Apply
            </Button>
            <Button
              type="button"
              color="danger"
              onClick={() => {
                reset();
                setFilter({
                  page_no: 1,
                  limit: RECON_LIMIT,
                  isFilter: false,
                });
              }}
            >
              Clear
            </Button>
          </Form>
        </CardHeader>
        <Table
          columns={activeColumns}
          filterColumns={filterColumns}
          isLoading={isLoading}
          data={activeData?.data || []}
          isData={!!activeData?.data?.length}
          count={activeData?.total_item || 0}
          pagination={filter}
          isExpandable={false}
          handlePaginationChange={(pagination) => {
            setFilter({
              ...filter,
              ...pagination,
            });
          }}
          paginationOptions={RECON_PAGINATION_OPTIONS}
          onColumnsChange={(cols) => {
            setFilterColumns(cols);
          }}
        >
          {activeData?.data?.map((val, idx) => {
            return (
              <TableRow
                key={val?.id || val?.fileId || idx}
                columns={activeColumns}
                filterColumns={filterColumns}
                isExpandable={false}
                item={val}
                onClick={activeTab === "file" ? handleRowClick : undefined}
                style={activeTab === "file" ? { cursor: "pointer" } : undefined}
              />
            );
          })}
        </Table>
      </Card>
      <AddStatementModal
        isModalOpen={isAddStatementOpen}
        onCloseModal={() => setIsAddStatementOpen(false)}
        bankOptions={getBankOptions()}
        onGet={onGetData}
      />
    </div>

  );
};

export default ReconciliationFile;
